/** One project's response on a scheduled pass, inside its folded turn: the Manager's
 * coordination note, each specialist once with its emails in place - including one that
 * did not work the site on this run but still has emails waiting. Decisions are not
 * shown here: an operator answers them in Next actions. See docs/Y2026-Q3/design-one-block-per-specialist.md. */
import {
  type ComponentType,
  createContext,
  type JSX,
  type ReactNode,
  useContext,
} from "react";
import type { Components, ExtraProps } from "react-markdown";
import { Link } from "react-router-dom";

import type { RunEmail, RunProjectView } from "../api/hooks/useStatefulAgents";
import {
  ANSWER_MARKER,
  DECISION_MARKER,
  EMAIL_MARKER,
} from "../utils/emailMarker";
import { projectScheduledPath } from "../routes/learningAgentPaths";
import { reviewAttrs } from "../utils/reviewState";
import { Markdown } from "./Markdown";
import { EmailCard, type OnReply } from "./RunItemCards";
import { RunChanges } from "./RunChanges";
import styles from "./ProjectResponse.module.css";
import { WrittenBeforeBranch } from "./AgentSlotBar";

interface Citations {
  components: Components;
  transform: (body: string) => string;
}

// The note a Manager with nothing to coordinate may still write.
const NOTHING_TO_COORDINATE = /^\W*nothing to coordinate\W*$/i;

// The note's prose, without the decision markers an older run placed in it.
const coordinationProse = (note: string): string =>
  note.replace(DECISION_MARKER, "").trim();

const hasCoordination = (note: string): boolean => {
  const prose = coordinationProse(note);
  return prose !== "" && !NOTHING_TO_COORDINATE.test(prose);
};

/** The email a specialist's draft became: as written, or merged into another. A
 * summary places its emails by draft id (team_reports.merge_specialist_reports). */
const emailFor = (draftId: string, emails: RunEmail[]): RunEmail | undefined =>
  emails.find((e) => e.draft_id === draftId) ??
  emails.find((e) => e.merges.includes(draftId));

/** The emails an answer places inline. */
const placedIn = (reply: string, emails: RunEmail[]): string[] =>
  [...reply.matchAll(EMAIL_MARKER)].flatMap((match) => {
    const email = emailFor(match[1], emails);
    return email ? [email.draft_id] : [];
  });

/** One specialist's answer, its emails shown where it places them. */
// A card link's destination: a fragment, as citation links use, so react-markdown
// keeps it.
const CARD_HREF = "#card:";
const LIST_ITEM = /^(\s*)(\d+[.)]|[-*+])\s+/;
const ONE_MARKER = new RegExp(
  `^${ANSWER_MARKER.source.replace(/^\^/, "").replace(/\$$/, "")}$`,
);

/** The answer with each card marker turned into a link on a paragraph of its own,
 * indented into the list item above it: rendered as one document, a numbered list a
 * card sits in carries on after the card rather than restarting unindented. */
const withCardSlots = (reply: string): string => {
  const out: string[] = [];
  let indent = 0;
  for (const line of reply.split("\n")) {
    const marker = line.match(ONE_MARKER);
    if (!marker) {
      if (line.trim()) {
        const item = line.match(LIST_ITEM);
        indent = item ? item[0].length : line.length - line.trimStart().length;
      }
      out.push(line);
      continue;
    }
    const pad = " ".repeat(indent);
    out.push("", `${pad}[card](${CARD_HREF}${marker[1]}:${marker[2]})`, "");
  }
  return out.join("\n");
};

type Anchor = ComponentType<JSX.IntrinsicElements["a"] & ExtraProps>;

// react-markdown renders each entry of `components` as a component TYPE, so a
// renderer rebuilt per render remounts everything under it - every card's open
// state and dialog with it, on each poll or keystroke. So the renderers are
// module-level, and what changes per render reaches them through this context.
const CardSlots = createContext<{
  card: (kind: string, key: string) => ReactNode;
  Cite: Anchor | undefined;
}>({ card: () => null, Cite: undefined });

const CardOrLink: Anchor = (props) => {
  const { card, Cite } = useContext(CardSlots);
  const { href } = props;
  if (href?.startsWith(CARD_HREF)) {
    const [kind, key] = href.slice(CARD_HREF.length).split(":");
    return <>{card(kind ?? "", key ?? "")}</>;
  }
  return Cite ? <Cite {...props} /> : <a href={href}>{props.children}</a>;
};

// A card is a block, so the paragraph that holds only its link is not a <p>.
const CardParagraph: NonNullable<Components["p"]> = ({ node, children }) => {
  const only = node?.children.length === 1 ? node.children[0] : undefined;
  const holdsCard =
    only?.type === "element" &&
    only.tagName === "a" &&
    String(only.properties?.href ?? "").startsWith(CARD_HREF);
  return holdsCard ? <>{children}</> : <p>{children}</p>;
};

const Answer = ({
  heading,
  review,
  reply,
  emails,
  withheld,
  projectName,
  onReply,
  citations,
}: {
  heading: ReactNode;
  /** Who a comment selected in the answer goes to (reviewAttrs). */
  review: Record<string, string>;
  reply: string;
  emails: RunEmail[];
  withheld: Record<string, string>;
  projectName: string;
  onReply: OnReply;
  citations: Citations;
}) => {
  const card = (kind: string, key: string): ReactNode => {
    // A decision an older run placed here is answered in Next actions, not on a run.
    if (kind === "decision") return null;
    // A reason may be empty, but the draft was still held back.
    const held = withheld[key];
    if (held !== undefined) {
      // Combined into another email: that one goes out, so it is shown here.
      const combined = emails.find((e) => e.merges.includes(key));
      // Holding it back left the earlier email to the same person standing.
      const kept = emails.filter((e) => e.kept_by === key);
      return (
        <div>
          <p className={styles.muted}>
            Email held back by the Manager{held ? `: ${held}` : "."}
          </p>
          {combined && (
            <EmailCard
              email={combined}
              onReply={onReply}
              inline
              projectName={projectName}
              defaultOpen={false}
            />
          )}
          {kept.map((e) => (
            <div key={e.draft_id}>
              <p className={styles.muted}>
                The earlier email to the same person is still waiting to be
                sent:
              </p>
              <EmailCard
                email={e}
                onReply={onReply}
                inline
                projectName={projectName}
                defaultOpen={false}
              />
            </div>
          ))}
        </div>
      );
    }
    const email = emailFor(key, emails);
    return email ? (
      <EmailCard
        email={email}
        onReply={onReply}
        inline
        projectName={projectName}
        defaultOpen={false}
      />
    ) : null;
  };
  const components: Components = {
    ...citations.components,
    a: CardOrLink,
    p: CardParagraph,
  };
  return (
    <section className={styles.specialist} {...review}>
      <h4 className={styles.heading}>{heading}</h4>
      <CardSlots.Provider
        value={{ card, Cite: citations.components.a as Anchor | undefined }}
      >
        <Markdown components={components}>
          {citations.transform(withCardSlots(reply || "(no answer)"))}
        </Markdown>
      </CardSlots.Provider>
    </section>
  );
};

// A comment on a specialist's answer goes to that specialist on that project.
const answerReview = (
  project: { project_id: string; project_name: string },
  agent: string,
  label: string,
): Record<string, string> =>
  reviewAttrs("conversation_reply", `${label}'s answer on ${project.project_name}`.slice(0, 200), {
    agent,
    doc: `project:${project.project_id}`,
  });

const shortDate = (iso: string): string =>
  new Date(iso).toLocaleDateString("en-GB", { day: "numeric", month: "short" });

const EmailList = ({
  heading,
  emails,
  projectName,
  onReply,
}: {
  heading: string;
  emails: RunEmail[];
  projectName: string;
  onReply: OnReply;
}) => (
  <section>
    <h4 className={styles.heading}>{heading}</h4>
    <div className={styles.cards}>
      {emails.map((email, i) => (
        <EmailCard
          key={email.draft_id || i}
          email={email}
          onReply={onReply}
          inline={false}
          projectName={projectName}
          defaultOpen={false}
        />
      ))}
    </div>
  </section>
);

/** A run from before the run page rendered records: the Manager's note and each
 * specialist's prose answer, with their emails in place. */
const LegacyAnswers = ({
  project,
  onReply,
  citations,
  runLinks,
}: {
  project: RunProjectView;
  onReply: OnReply;
  citations: Citations;
  runLinks: boolean;
}) => (
  <>
    {/* Silent unless the Manager has something between the specialists to say, or a
          problem with the emails. */}
    {(hasCoordination(project.coordination) ||
      project.email_problems.length > 0) && (
      <section className={styles.coordination}>
        {hasCoordination(project.coordination) && (
          <>
            <h4 className={styles.heading}>Manager</h4>
            <Markdown components={citations.components}>
              {citations.transform(coordinationProse(project.coordination))}
            </Markdown>
          </>
        )}
        {project.email_problems.map((problem) => (
          <p key={problem} className={styles.warning}>
            {problem}
          </p>
        ))}
      </section>
    )}
    {project.specialists.map((report) => (
      <Answer
        key={report.agent}
        review={runLinks ? answerReview(project, report.agent, report.label) : {}}
        heading={
          <>
            {report.label}
            {!report.concluded && (
              <span className={styles.warning}>
                {" "}
                · did not complete its pass
              </span>
            )}
          </>
        }
        reply={report.reply}
        emails={project.emails}
        withheld={project.withheld}
        projectName={project.project_name}
        onReply={onReply}
        citations={citations}
      />
    ))}
    {project.earlier.map((block) => {
      // Ran without concluding: its last complete answer still stands.
      const ran = project.specialists.some((s) => s.agent === block.agent);
      return (
        <Answer
          key={`earlier-${block.agent}`}
          review={runLinks ? answerReview(project, block.agent, block.label) : {}}
          heading={
            <>
              {block.label} ·{" "}
              {ran
                ? "did not complete its pass on this run"
                : "did not work this site on this run"}{" "}
              ·{" "}
              {runLinks ? (
                <>
                  <Link
                    to={projectScheduledPath(project.project_id, block.run_id)}
                  >
                    its {ran ? "last complete " : ""}
                    answer from {shortDate(block.created_at)}
                  </Link>
                  <WrittenBeforeBranch at={block.created_at} />
                </>
              ) : (
                <>
                  its {ran ? "last complete " : ""}
                  answer from {shortDate(block.created_at)}
                </>
              )}
            </>
          }
          reply={block.reply}
          emails={block.emails}
          withheld={block.withheld}
          projectName={project.project_name}
          onReply={onReply}
          citations={citations}
        />
      );
    })}
  </>
);

const authorOf = (email: RunEmail): string => email.author_label || "the agent";

/** ``runLinks`` false where the page is the project's briefing, not a run: it then
 * names earlier runs without linking down into them. */
export const ProjectResponse = ({
  runId,
  project,
  onReply,
  citations,
  runLinks = true,
}: {
  runId: string;
  project: RunProjectView;
  onReply: OnReply;
  citations: Citations;
  runLinks?: boolean;
}) => {
  // What no specialist's answer placed: emails from before placement existed, or one a
  // specialist wrote on a later delegation than the answer an old run kept. Listed under
  // its author, since an unowned email reads as nobody's.
  const placed = new Set(
    project.specialists.flatMap((s) => placedIn(s.reply || "", project.emails)),
  );
  const unplaced = project.emails.filter(
    (e) => !e.carried && !placed.has(e.draft_id),
  );
  // Waiting from an earlier run with no earlier answer placing it (a conversation's
  // email, say): listed under its author.
  const inEarlier = new Set(
    project.earlier.flatMap((block) => placedIn(block.reply, block.emails)),
  );
  const loose = project.emails.filter(
    (e) => e.carried && !e.kept_by && !inEarlier.has(e.draft_id),
  );
  return (
    <div className={styles.response}>
      {runLinks && project.latest_run_id && project.latest_run_id !== runId && (
        <p className={styles.outOfDate}>
          This is not the latest analysis for this site -{" "}
          <Link
            to={projectScheduledPath(project.project_id, project.latest_run_id)}
          >
            view the latest analysis
          </Link>
        </p>
      )}
      {project.by_reference && project.changes ? (
        <RunChanges
          changes={project.changes}
          projectId={project.project_id}
          inRun={runLinks}
        />
      ) : (
        <LegacyAnswers
          project={project}
          onReply={onReply}
          citations={citations}
          runLinks={runLinks}
        />
      )}
      {[...new Set(unplaced.map(authorOf))].map((author) => (
        <EmailList
          key={`unplaced-${author}`}
          heading={`${author} · more emails from this run`}
          emails={unplaced.filter((e) => authorOf(e) === author)}
          projectName={project.project_name}
          onReply={onReply}
        />
      ))}
      {[...new Set(loose.map(authorOf))].map((author) => (
        <EmailList
          key={author}
          heading={`${author} · emails still waiting from earlier runs`}
          emails={loose.filter((e) => authorOf(e) === author)}
          projectName={project.project_name}
          onReply={onReply}
        />
      ))}
    </div>
  );
};
